import React from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  useTheme,
  Card,
  CardContent,
  Tooltip,
} from '@mui/material';
import { PartnerAvailability } from '@marageti/z4-sdk/lib/travel';
import { OutlinedIcons, Icons } from '@marageti/z4-lib';
import EventCard from '../../partner/event-card';

export type DialogState = 'closed' | 'open' | 'closing' | 'opening';

const {
  OutlinedCalendarIcon,
} = OutlinedIcons;

type PartnerCapacityDialogViewProps = {
  dialogState: DialogState;
  setDialogState: (dialogState: DialogState) => void;
  handleOpen: () => void;
  handleClose: () => void;
  currentCapacity: number;
  adjustedCapacity: number;
  partnerAvailability: PartnerAvailability[] | undefined;
  children: (props: { handleOpen: () => void }) => React.ReactNode;
  isLoading: boolean;
  hasError: boolean;
};

const PartnerCapacityDialogView = ({
  dialogState,
  setDialogState,
  handleOpen,
  handleClose,
  currentCapacity,
  adjustedCapacity,
  partnerAvailability,
  children,
  isLoading,
  hasError,
}: PartnerCapacityDialogViewProps) => {
  const theme = useTheme();

  return (
    <>
      {children({ handleOpen })}
      <Dialog
        open={dialogState === 'open' || dialogState === 'opening'}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        TransitionProps={{
          onEntered: () => {
            setDialogState('open');
          },
          onExited: () => {
            setDialogState('closed');
          },
        }}
      >
        <DialogTitle sx={{ pb: 2 }}>Capacity</DialogTitle>
        <DialogContent>
          <Stack
            direction="row"
            spacing={2}
            sx={{
              mb: 4,
            }}
          >
            <Card sx={{ width: '100%' }}>
              <CardContent>
                <Typography variant="body2">
                  Current Capacity
                  <Tooltip title="Current Capacity = Max Capacity minus trips matched in the past 7 days." placement="top" enterTouchDelay={50}>
                    <span>
                      <Icons.ExclamationCircle
                        color="action"
                        sx={{
                          width: '16px',
                          height: '16px',
                          ml: 1,
                          verticalAlign: 'text-top',
                        }}
                      />
                    </span>
                  </Tooltip>
                </Typography>
                <Typography variant="h3">
                  {currentCapacity}
                  {' '}
                  Trips
                  {' '}
                  / Week
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ width: '100%' }}>
              <CardContent>
                <Typography variant="body2">
                  Max Capacity
                  <Tooltip title="Maximum capacity set by partner availability" placement="top" enterTouchDelay={50}>
                    <span>
                      <Icons.ExclamationCircle
                        color="action"
                        sx={{
                          width: '16px',
                          height: '16px',
                          ml: 1,
                          verticalAlign: 'text-top',
                        }}
                      />
                    </span>
                  </Tooltip>
                </Typography>
                <Typography variant="h3">
                  {adjustedCapacity}
                  {' '}
                  Trips
                  {' '}
                  / Week
                </Typography>
              </CardContent>
            </Card>
          </Stack>

          {partnerAvailability
          && partnerAvailability.length > 0
            ? (
              <Box>
                <Typography variant="h4" sx={{ mb: 1 }}>Upcoming Events</Typography>
                <Stack
                  spacing={1}
                  sx={{ mb: 4 }}
                >
                  {partnerAvailability && partnerAvailability.map((listItem) => (
                    <EventCard
                      key={listItem.limitType + listItem.startDate + listItem.endDate}
                      availabilityItem={listItem}
                      capacity={currentCapacity}
                    />
                  ))}
                </Stack>
              </Box>
            ) : (
              <Box
                sx={{
                  height: '377px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                }}
              >
                {hasError && (
                  <Typography variant="body1" color="text.secondary">Error loading capacity</Typography>
                )}
                {!isLoading && !hasError && (
                <>
                  <OutlinedCalendarIcon sx={{
                    color: theme.palette.common.black50, width: '48px', height: '48px', strokeWeight: '1px !important', mb: 1.5,
                  }}
                  />
                  <Typography variant="body1" color="text.secondary">There are no events for the next 12 months</Typography>
                </>
                )}
              </Box>
            )}
        </DialogContent>
        <DialogActions
          sx={{
            borderTop: `1px solid ${theme.palette.divider}`,
            py: '20px',
          }}
        >
          <Button variant="outlined" onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PartnerCapacityDialogView;
